/**
 * Cross-tab coordination for browser notifications.
 *
 * The unread feed runs in EVERY open tab (it hangs off AppLayout), so ten tabs
 * meant ten `new Notification(...)` calls per inbound message. The
 * per-conversation `tag` collapses them into one visible toast, but each call
 * still re-alerts (sound/vibration on most platforms), so the operator got the
 * same message announced ten times.
 *
 * Two gates fix that, both cheap and both fail-open on the safe side:
 *
 * 1. LEADER ELECTION — one tab holds a Web Lock for the origin and is the only
 *    one allowed to notify. The lock is released when that tab closes and the
 *    next waiter is granted it automatically, so leadership survives closing
 *    the "wrong" tab with no heartbeat of our own. Where `navigator.locks` is
 *    unavailable (non-secure context) every tab stays eligible and the `tag`
 *    collapse is the only defence — the pre-existing behaviour.
 *
 * 2. ANY-TAB VISIBILITY — a notification for a message the operator can
 *    already see is noise. `document.hidden` only speaks for the calling tab,
 *    and the leader is frequently not the tab being looked at, so every tab
 *    heartbeats a timestamp into localStorage while visible and the leader
 *    treats a recent beat from ANY tab as "the app is on screen".
 */

const LOCK_NAME = 'dolpheen:messaging-notifications'
const VISIBLE_KEY = 'messaging:lastVisibleAt'
/** Slightly over two heartbeats, so one missed beat is not read as "hidden". */
const VISIBLE_TTL_MS = 9000
const HEARTBEAT_MS = 4000

let isLeader = false

/** True while this tab, or any other tab of the app, is on screen. */
function isAnyTabVisible(): boolean {
  if (!document.hidden) {
    return true
  }

  try {
    const raw = localStorage.getItem(VISIBLE_KEY)
    return raw !== null && Date.now() - Number(raw) < VISIBLE_TTL_MS
  } catch {
    // No storage — assume nothing is visible rather than swallowing the
    // notification: a missed alert is worse than a duplicate one.
    return false
  }
}

/** The single gate the notification path asks before alerting. */
export function canFireNotification(): boolean {
  return isLeader && !isAnyTabVisible()
}

/** Callers holding the coordinator: the unread feed and the reminder feed. */
let holders = 0
let stopRunning: (() => void) | null = null

/**
 * Starts the visibility heartbeat and joins the leader election. Shared by
 * every feed that alerts: the first caller starts it, later callers only
 * join, and it stops with the last teardown. Each returned teardown counts
 * once, however often it is called.
 */
export function startNotificationCoordinator(): () => void {
  holders += 1
  if (holders === 1) {
    stopRunning = runNotificationCoordinator()
  }

  let isReleased = false
  return () => {
    if (isReleased) {
      return
    }

    isReleased = true
    holders -= 1
    if (holders === 0) {
      stopRunning?.()
      stopRunning = null
    }
  }
}

/** The heartbeat and the lock request; returns the teardown (which also hands leadership on). */
function runNotificationCoordinator(): () => void {
  const beat = () => {
    if (document.hidden) {
      return
    }

    try {
      localStorage.setItem(VISIBLE_KEY, String(Date.now()))
    } catch {
      // Ignore persistence failures — see isAnyTabVisible.
    }
  }

  beat()
  const timer = setInterval(beat, HEARTBEAT_MS)
  document.addEventListener('visibilitychange', beat)

  const controller = new AbortController()
  let releaseLock: (() => void) | null = null
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks

  if (locks) {
    void locks
      .request(
        LOCK_NAME,
        { signal: controller.signal },
        () =>
          // Held for the tab's lifetime: the callback's promise IS the lock, so
          // it only settles on teardown. An AbortSignal cancels a PENDING
          // request only, which is why the resolver is kept for the granted
          // case.
          new Promise<void>((resolve) => {
            isLeader = true
            releaseLock = resolve
          }),
      )
      .catch(() => undefined)
      .finally(() => {
        isLeader = false
      })
  } else {
    isLeader = true
  }

  return () => {
    clearInterval(timer)
    document.removeEventListener('visibilitychange', beat)
    controller.abort()
    releaseLock?.()
    isLeader = false
  }
}
